import {
  useRef,
  useCallback,
  useEffect,
  useState,
  ChangeEvent,
  KeyboardEvent,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useAuth } from '@clerk/clerk-react';
import { FiImage, FiSend, FiX, FiFilm } from 'react-icons/fi';
import { track } from '@/lib/analytics';
import { useTempCheck } from '@/hooks/useTempCheck';
import {
  checkTempLevel,
  type TempCheckResult,
  type TempCheckScope,
} from '@/lib/tempcheck-api';
import { TempCheckBar } from './TempCheckBar';

type MessageInputProps = {
  messageText: string;
  onMessageTextChange: (text: string) => void;
  selectedImages: File[];
  onSelectedImagesChange: (images: File[]) => void;
  onSend: () => void;
  isSending: boolean;
  disabled?: boolean;
  tempCheckScope?: TempCheckScope;
};

export interface MessageInputHandle {
  focus: () => void;
}

const MAX_ATTACHMENTS = 3;
const ACCEPT_MEDIA = 'image/*,video/mp4,video/quicktime,video/webm';

function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/');
}

export const MessageInput = forwardRef<MessageInputHandle, MessageInputProps>(
  function MessageInput(
    {
      messageText,
      onMessageTextChange,
      selectedImages,
      onSelectedImagesChange,
      onSend,
      isSending,
      disabled = false,
      tempCheckScope = { kind: 'main' },
    },
    ref
  ) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
    }));

    const { getToken } = useAuth();
    const { enabled: tempCheckEnabled } = useTempCheck();
    const [tempResult, setTempResult] = useState<TempCheckResult | null>(null);
    const [tempLoading, setTempLoading] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        abortRef.current?.abort();
      };
    }, []);

    const debouncedCheck = useCallback(
      (text: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        abortRef.current?.abort();

        if (!tempCheckEnabled) {
          setTempResult(null);
          setTempLoading(false);
          return;
        }
        const trimmed = text.trim();
        if (trimmed.length < 3) {
          setTempResult(null);
          setTempLoading(false);
          return;
        }
        setTempLoading(true);
        debounceRef.current = setTimeout(async () => {
          const controller = new AbortController();
          abortRef.current = controller;
          try {
            const result = await checkTempLevel(
              getToken,
              trimmed,
              tempCheckScope,
              controller.signal
            );
            setTempResult(result);
            track('tone_check_result', {
              level: result.level,
              score: result.score,
              has_suggestion: !!result.suggestion,
              context_size: result.context_size ?? 0,
              scope: tempCheckScope.kind,
            });
          } catch {
            if (!controller.signal.aborted) {
              setTempResult(null);
            }
          } finally {
            if (!controller.signal.aborted) {
              setTempLoading(false);
            }
          }
        }, 500);
      },
      [getToken, tempCheckEnabled, tempCheckScope]
    );

    const handleTextChange = useCallback(
      (text: string) => {
        onMessageTextChange(text);
        debouncedCheck(text);
      },
      [onMessageTextChange, debouncedCheck]
    );

    const handleUseSuggestion = useCallback(
      (suggestion: string) => {
        track('tone_check_rewrite_used', {
          level: tempResult?.level,
          score: tempResult?.score,
        });
        onMessageTextChange(suggestion);
        setTempResult(null);
        debouncedCheck(suggestion);
      },
      [onMessageTextChange, debouncedCheck, tempResult]
    );

    const handleFileSelect = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        const remaining = MAX_ATTACHMENTS - selectedImages.length;
        if (remaining <= 0) {
          alert(`You can attach up to ${MAX_ATTACHMENTS} files.`);
          return;
        }

        const newFiles = Array.from(files).slice(0, remaining);
        onSelectedImagesChange([...selectedImages, ...newFiles]);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
      [selectedImages, onSelectedImagesChange]
    );

    const removeFile = useCallback(
      (index: number) => {
        onSelectedImagesChange(selectedImages.filter((_, i) => i !== index));
      },
      [selectedImages, onSelectedImagesChange]
    );

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          const trimmed = messageText.trim();
          if ((trimmed || selectedImages.length > 0) && !isSending && !disabled) {
            onSend();
          }
        }
      },
      [messageText, selectedImages.length, isSending, disabled, onSend]
    );

    const handleSend = useCallback(() => {
      const trimmed = messageText.trim();
      if ((!trimmed && selectedImages.length === 0) || isSending || disabled) return;
      if (tempResult && (tempResult.level === 'warm' || tempResult.level === 'hot')) {
        track('tone_check_sent_with_warning', {
          level: tempResult.level,
          score: tempResult.score,
        });
      }
      setTempResult(null);
      onSend();
    }, [messageText, selectedImages.length, isSending, disabled, onSend, tempResult]);

    const canSend =
      (messageText.trim() || selectedImages.length > 0) && !isSending && !disabled;

    return (
      <div className='px-4 py-3 border-t border-gray-200 bg-sky-light'>
        {tempCheckEnabled && !disabled && (
          <TempCheckBar
            level={tempResult?.level}
            label={tempResult?.label}
            suggestion={tempResult?.suggestion}
            isLoading={tempLoading}
            tooShort={messageText.trim().length < 3}
            onUseSuggestion={handleUseSuggestion}
          />
        )}

        {selectedImages.length > 0 && (
          <div className='flex gap-2 mb-2'>
            {selectedImages.map((file, index) => (
              <div key={index} className='relative w-15 h-15'>
                {isVideoFile(file) ? (
                  <div className='w-[60px] h-[60px] rounded-lg bg-gray-800 flex items-center justify-center'>
                    <FiFilm className='w-6 h-6 text-white' />
                  </div>
                ) : (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Selected ${index + 1}`}
                    className='w-[60px] h-[60px] object-cover rounded-lg'
                  />
                )}
                <button
                  type='button'
                  onClick={() => removeFile(index)}
                  className='absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors'
                >
                  <FiX className='w-3 h-3 text-white' />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className='flex items-end gap-2.5'>
          <button
            type='button'
            onClick={() => fileInputRef.current?.click()}
            disabled={selectedImages.length >= MAX_ATTACHMENTS || disabled}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              selectedImages.length >= MAX_ATTACHMENTS || disabled
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-primary hover:bg-primary/10'
            }`}
          >
            <FiImage className='w-5 h-5' />
          </button>

          <input
            ref={fileInputRef}
            type='file'
            accept={ACCEPT_MEDIA}
            multiple
            onChange={handleFileSelect}
            className='hidden'
          />

          <textarea
            ref={textareaRef}
            value={messageText}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={disabled ? 'Chat is closed' : 'Type a message...'}
            disabled={isSending || disabled}
            rows={1}
            className={`flex-1 px-3.5 py-2.5 rounded-[20px] border resize-none text-base leading-5 max-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary/30 ${
              disabled
                ? 'bg-gray-100 text-gray-400 border-gray-200'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            style={{ minHeight: '40px' }}
          />

          <button
            type='button'
            onClick={handleSend}
            disabled={!canSend}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              canSend
                ? 'bg-primary text-white hover:bg-primary-hover'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSending ? (
              <div className='w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin' />
            ) : (
              <FiSend className='w-5 h-5' />
            )}
          </button>
        </div>
      </div>
    );
  }
);
